import React, { useEffect, useState } from 'react';
import { Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';

const EmailChangeDialog = ({ open, onOpenChange, user, tenantId, onRequested }) => {
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNewEmail('');
    setResult(null);
    setBusy(false);
  }, [open, user?.id]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { new_email: newEmail.trim() };
      if (tenantId) payload.tenant_id = tenantId;
      const { data } = await axiosInstance.post(`/users/${encodeURIComponent(user.id)}/email-change/request`, payload);
      setResult(data);
      toast.success('Verification email sent to the new address');
      onRequested?.(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the email change');
    } finally {
      setBusy(false);
    }
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Change sign-in email</DialogTitle></DialogHeader>
      {result ? <div className="space-y-4">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
          <strong className="flex items-center gap-2"><MailCheck size={17}/>Verification sent</strong>
          <p className="mt-2">Qflow sent a single-use confirmation link to <strong>{result.new_email}</strong>. No account details have changed yet.</p>
          <p className="mt-2 text-xs">The link expires {new Date(result.expires_at).toLocaleString()}.</p>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-600">
          After confirmation, the same profile, UI access, teams, password and history are retained. Existing sessions are signed out and the old email stops working.
        </div>
        <div className="flex justify-end"><Button onClick={() => onOpenChange(false)}>Done</Button></div>
      </div> : <form className="space-y-4" onSubmit={submit}>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
          <strong className="flex items-center gap-2"><ShieldCheck size={17}/>This does not create another user</strong>
          <p className="mt-1">The new mailbox must confirm ownership before Qflow changes the existing account.</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">Current sign-in email</p>
          <p className="mt-1 font-medium text-slate-900">{user?.email}</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="new-sign-in-email">New sign-in email</label>
          <Input id="new-sign-in-email" type="email" autoComplete="email" value={newEmail} onChange={event => setNewEmail(event.target.value)} required autoFocus placeholder="name@organisation.com" />
        </div>
        <p className="text-xs text-slate-500">Qflow will notify the current address. The password remains unchanged.</p>
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button disabled={busy || !newEmail.trim()}>{busy ? <><Loader2 size={16} className="animate-spin"/>Sending…</> : 'Send verification email'}</Button></div>
      </form>}
    </DialogContent>
  </Dialog>;
};

export default EmailChangeDialog;
